'use client';

import * as React from 'react';
import type { Editor } from '@tiptap/react';
import type { Content, UseEditorOptions } from '@tiptap/react';
import { StarterKit } from '@tiptap/starter-kit';
import { useEditor } from '@tiptap/react';
import { Typography } from '@tiptap/extension-typography';
import { Placeholder } from '@tiptap/extension-placeholder';
import { Underline } from '@tiptap/extension-underline';
import { TextStyle } from '@tiptap/extension-text-style';
import {
  Link,
  Image,
  HorizontalRule,
  CodeBlockLowlight,
  Selection,
  Color,
  UnsetAllMarks,
  ResetMarksOnEnter,
  FileHandler,
} from '../extensions';
import { cn } from '@/lib/utils';
import { fileToBase64, getOutput, getOutputEditor, randomId } from '../utils';
import { useThrottle } from '../hooks/use-throttle';
import { toast } from 'sonner';
import { useEffect } from 'react';

export interface UseMinimalTiptapEditorProps extends UseEditorOptions {
  value?: Content;
  output?: 'html' | 'json' | 'text';
  placeholder?: string;
  editorClassName?: string;
  throttleDelay?: number;
  onUpdate?: (content: Content) => void;
  onBlur?: (content: Content) => void;
}

const createExtensions = (placeholder: string, readonly: boolean) => [
  StarterKit.configure({
    horizontalRule: false,
    codeBlock: false,
    paragraph: { HTMLAttributes: { class: 'text-node' } },
    heading: { HTMLAttributes: { class: 'tiptap-heading' }, levels: [1, 2, 3, 4, 5, 6] },
    blockquote: { HTMLAttributes: { class: 'block-node' } },
    bulletList: { HTMLAttributes: { class: 'list-node' } },
    orderedList: { HTMLAttributes: { class: 'list-node' } },
    code: { HTMLAttributes: { class: 'inline', spellcheck: 'false' } },
    dropcursor: { width: 2, class: 'ProseMirror-dropcursor border' },
  }),
  Link.configure({
    openOnClick: readonly,
    autolink: !readonly,
  }),
  Underline,
  Image.configure({
    allowedMimeTypes: ['image/*'],
    maxFileSize: 5 * 1024 * 1024,
    allowBase64: true,
    uploadFn: readonly
      ? undefined
      : async (file) => {
          // NOTE: This is a fake upload function. Replace this with your own upload logic.
          // This function should return the uploaded image URL.

          // wait 3s to simulate upload
          await new Promise((resolve) => setTimeout(resolve, 3000));
          const src = await fileToBase64(file);

          // either return { id: string | number, src: string } or just src
          // return src;
          return { id: randomId(), src };
        },
    onImageRemoved: readonly
      ? undefined
      : ({ id, src }) => {
          // eslint-disable-next-line no-console
          console.log('Image removed', { id, src });
        },
    onValidationError: readonly
      ? undefined
      : (errors) => {
          errors.forEach((error) => {
            toast.error('Image validation error', {
              position: 'bottom-right',
              description: error.reason,
            });
          });
        },
    onActionSuccess: readonly
      ? undefined
      : ({ action }) => {
          const mapping = {
            copyImage: 'Copy Image',
            copyLink: 'Copy Link',
            download: 'Download',
          };
          toast.success(mapping[action], {
            position: 'bottom-right',
            description: 'Image action success',
          });
        },
    onActionError: readonly
      ? undefined
      : (error, { action }) => {
          const mapping = {
            copyImage: 'Copy Image',
            copyLink: 'Copy Link',
            download: 'Download',
          };
          toast.error(`Failed to ${mapping[action]}`, {
            position: 'bottom-right',
            description: error.message,
          });
        },
  }),
  ...(!readonly
    ? [
        FileHandler.configure({
          allowBase64: true,
          allowedMimeTypes: ['image/*'],
          maxFileSize: 5 * 1024 * 1024,
          onDrop: (editor, files, pos) => {
            files.forEach(async (file) => {
              const src = await fileToBase64(file);
              editor.commands.insertContentAt(pos, {
                type: 'image',
                attrs: { src },
              });
            });
          },
          onPaste: (editor, files) => {
            files.forEach(async (file) => {
              const src = await fileToBase64(file);
              editor.commands.insertContent({
                type: 'image',
                attrs: { src },
              });
            });
          },
          onValidationError: (errors) => {
            errors.forEach((error) => {
              toast.error('Image validation error', {
                position: 'bottom-right',
                description: error.reason,
              });
            });
          },
        }),
      ]
    : []),
  Color,
  TextStyle,
  Selection,
  Typography,
  UnsetAllMarks,
  HorizontalRule,
  ResetMarksOnEnter,
  CodeBlockLowlight,
  Placeholder.configure({ placeholder: () => placeholder }),
];

export const useMinimalTiptapEditor = ({
  value,
  output = 'html',
  placeholder = '',
  editorClassName,
  throttleDelay = 0,
  onUpdate,
  onBlur,
  editable,
  ...props
}: UseMinimalTiptapEditorProps) => {
  const readonly = !editable;

  const throttledSetValue = useThrottle((value: Content) => onUpdate?.(value), throttleDelay);

  const handleUpdate = React.useCallback(
    (editor: Editor) => throttledSetValue(getOutputEditor(editor, output)),
    [output, throttledSetValue]
  );

  const handleCreate = React.useCallback(
    (editor: Editor) => {
      if (value && editor.isEmpty) {
        editor.commands.setContent(value);
      }
    },
    [value]
  );

  const handleBlur = React.useCallback(
    (editor: Editor) => onBlur?.(getOutput(editor, output)),
    [output, onBlur]
  );

  const editor = useEditor({
    extensions: createExtensions(placeholder, readonly),
    editorProps: {
      attributes: {
        autocomplete: 'off',
        autocorrect: 'off',
        autocapitalize: 'off',
        class: cn(
          'focus:outline-none py-6 px-8 prose prose-base prose-blue prose-headings:scroll-mt-[80px]',
          editorClassName
        ),
      },
      ...(readonly
        ? {
            handleDOMEvents: {
              dragstart: () => true, // block drag
              drop: () => true, // block drop
              paste: () => true, // block paste
              mousedown: (_view, event) => {
                const el = event.target as HTMLElement;
                // cegah interaksi pada image/figure/resize handles
                if (
                  el.closest('figure[data-type="image"]') ||
                  el.closest('img') ||
                  el.closest('[data-resize-handle]') ||
                  el.closest('.tiptap-image__resize-handle')
                ) {
                  event.preventDefault();
                  return true;
                }
                return false;
              },
            },
          }
        : {}),
      ...props.editorProps,
    },
    editable,
    onUpdate: ({ editor }) => handleUpdate(editor),
    onCreate: ({ editor }) => handleCreate(editor),
    onBlur: ({ editor }) => handleBlur(editor),
    ...props,
  });

  // Update editable state if/when it changes
  useEffect(() => {
    if (!editor || editor.isDestroyed || editor.isEditable === editable) return;
    // We use queueMicrotask to avoid any flushSync console errors as
    // mentioned here (though setEditable shouldn't trigger them in practice)
    // https://github.com/ueberdosis/tiptap/issues/3764#issuecomment-1546854730
    queueMicrotask(() => editor.setEditable(editable || false));
  }, [editable, editor]);

  return editor;
};

export default useMinimalTiptapEditor;
